import { type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type AdmissionOffer } from 'AdmissionOffer';
import { type CohortSectionOffer } from 'CohortSectionOffer';
import { selectablePaymentSituationForTuitionPlanId } from 'PaymentSituation';
import { AoiRecordType } from 'ProgramAoi';
import { isPartialScholarship } from 'Scholarship';
import { Text } from 'Text';
import { type TuitionContract } from 'TuitionContract';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { type CurrentUser, getAoiRecord, getCohort, getTuitionContract } from 'Users';
import { CohortRegistrationTuitionLedger } from '../../application_status/components/CohortRegistrationTuitionLedger';
import { LOCALE_PREFIX, stripeProductIdFor } from '../constants';
import { useCohortDates } from '../useCohortDates';
import { useStripeCheckoutSession } from '../useStripeCheckoutSession';
import { type CohortDates } from '../TuitionAndRegistration.types';
import { RegisterButton } from './RegisterButton';
import {
    REGISTRATION_ACTION_WIDTH,
    RegistrationCard,
    RegistrationDateStrip,
    RegistrationFooterPanel,
    RegistrationGraphic,
    RegistrationHeader,
    RegistrationNarrative,
} from './RegistrationLayout';

/*
    The learner already registered and picked a tuition plan, so this screen only reopens Stripe
    Checkout. Stripe Checkout collects the payment method.

    See https://trello.com/c/4pcyVnov.
*/
export function CohortRegistrationNoPlanSelection(): ReactElement | null {
    const currentUser = useRequiredCurrentUser();

    const cohort = getCohort(currentUser);
    const tuitionContract = getTuitionContract(currentUser);

    /*
        The active AOI record is a ProgramInclusion here, because the learner has registered. Reach
        back to the AdmissionOffer for the payment situations.
    */
    const admissionOffer = getAoiRecord(currentUser, AoiRecordType.AdmissionOffer);
    const cohortSectionOffer = admissionOffer?.cohortSectionOffers?.[0];

    if (!cohort || !admissionOffer || !cohortSectionOffer || !tuitionContract) return null;

    return (
        <NoPlanSelectionBody
            currentUser={currentUser}
            admissionOffer={admissionOffer}
            cohortSectionOffer={cohortSectionOffer}
            tuitionContract={tuitionContract}
        />
    );
}

type NoPlanSelectionBodyProps = {
    currentUser: CurrentUser;
    admissionOffer: AdmissionOffer;
    cohortSectionOffer: CohortSectionOffer;
    tuitionContract: TuitionContract;
};

function NoPlanSelectionBody({
    currentUser,
    admissionOffer,
    cohortSectionOffer,
    tuitionContract,
}: NoPlanSelectionBodyProps): ReactElement {
    const dates = useCohortDates(currentUser);
    const { processingRegistration, registerRequiresRedirectToBrowser, openStripeCheckoutSessionOrBrowser } =
        useStripeCheckoutSession();

    const selectedPaymentSituation = selectablePaymentSituationForTuitionPlanId(
        cohortSectionOffer.studentSelectablePaymentSituations,
        tuitionContract.tuitionPlan.id,
    );

    const handleRegister = () => {
        openStripeCheckoutSessionOrBrowser({
            admission_offer_id: admissionOffer.id,
            product_id: stripeProductIdFor(admissionOffer),
            selected_tuition_plan_id: tuitionContract.tuitionPlan.id,
            tuition_contract_id: tuitionContract.id,
        });
    };

    return (
        <RegistrationCard>
            <NoPlanSelectionIntro
                admissionOffer={admissionOffer}
                cohortSectionOffer={cohortSectionOffer}
                tuitionContract={tuitionContract}
                formattedNetRequiredPayment={selectedPaymentSituation?.paymentInformation.formattedNetRequiredPayment}
                registerRequiresRedirectToBrowser={registerRequiresRedirectToBrowser}
            />

            {dates ? <ReRegisterDeadline dates={dates} /> : null}

            {dates?.registerEnabled ? (
                <RegistrationFooterPanel>
                    <div className="flex justify-center">
                        <RegisterButton
                            processingRegistration={processingRegistration}
                            registerRequiresRedirectToBrowser={registerRequiresRedirectToBrowser}
                            isDisabled={processingRegistration}
                            onPress={handleRegister}
                            className={`w-full ${REGISTRATION_ACTION_WIDTH}`}
                        />
                    </div>
                </RegistrationFooterPanel>
            ) : null}
        </RegistrationCard>
    );
}

type NoPlanSelectionIntroProps = {
    admissionOffer: AdmissionOffer;
    cohortSectionOffer: CohortSectionOffer;
    tuitionContract: TuitionContract;
    formattedNetRequiredPayment?: string;
    registerRequiresRedirectToBrowser: boolean;
};

// The scholarship banner, the narrative copy, the tuition ledger and how much is paid so far.
function NoPlanSelectionIntro({
    admissionOffer,
    cohortSectionOffer,
    tuitionContract,
    formattedNetRequiredPayment,
    registerRequiresRedirectToBrowser,
}: NoPlanSelectionIntroProps): ReactElement {
    const { t } = useTranslation('back_royal');

    /*
        The scholarship on the signed contract drives the copy here, not the one on the offer. The
        learner already registered, so the contract is the source of truth.
    */
    const hasPartialScholarship = isPartialScholarship(tuitionContract.cumulativeScholarship);
    const scholarshipSuffix = hasPartialScholarship ? 'partial_scholarship' : 'no_scholarship';
    const numOfferedScholarships = cohortSectionOffer.offeredScholarships?.length ?? 0;

    return (
        <>
            <RegistrationHeader>
                <RegistrationGraphic
                    titleKey={`${LOCALE_PREFIX}.graphic_${scholarshipSuffix}_title`}
                    subtitleKey={`${LOCALE_PREFIX}.graphic_${scholarshipSuffix}_subtitle`}
                    scholarshipAmount={cohortSectionOffer.cumulativeOfferedScholarship?.amount}
                    numOfferedScholarships={numOfferedScholarships}
                />

                <RegistrationNarrative
                    tuitionDetailsLabelKey={`${LOCALE_PREFIX}.${
                        hasPartialScholarship ? 'tuition_and_scholarship_details' : 'tuition_details'
                    }`}
                    narrativeKey={`${LOCALE_PREFIX}.narrative_${scholarshipSuffix}`}
                    numOfferedScholarships={numOfferedScholarships}
                >
                    {registerRequiresRedirectToBrowser ? (
                        <Text as="div" className="mt-4 text-xs text-slate-grey">
                            {t(`${LOCALE_PREFIX}.new_browser_register`)}
                        </Text>
                    ) : null}
                </RegistrationNarrative>
            </RegistrationHeader>

            <CohortRegistrationTuitionLedger
                admissionOffer={admissionOffer}
                cohortSectionOffer={cohortSectionOffer}
                className="-mx-5 mt-5 px-5"
            />

            <AmountPaid
                netAmountPaid={tuitionContract.netTuitionAmount}
                formattedNetRequiredPayment={formattedNetRequiredPayment}
            />
        </>
    );
}

// How much of the tuition the learner has paid so far.
function AmountPaid({
    netAmountPaid,
    formattedNetRequiredPayment,
}: {
    netAmountPaid: number;
    formattedNetRequiredPayment?: string;
}): ReactElement {
    const { t } = useTranslation('back_royal');

    return (
        <Text
            as="p"
            className="-mx-5 mb-0 border-t border-slate-grey-200 px-5 py-5 text-center text-xs text-grey-darker"
        >
            {t(`${LOCALE_PREFIX}.paid_x_of_x`, {
                netAmountPaid: netAmountPaid.toLocaleString(),
                netRequiredPayment: formattedNetRequiredPayment,
            })}
        </Text>
    );
}

/*
    This screen shows the deadline with the class start date, unlike plan selection, and drops it once
    registration reopens.
*/
function ReRegisterDeadline({ dates }: { dates: CohortDates }): ReactElement {
    const { t } = useTranslation('back_royal');

    if (dates.beforeRegistrationDeadline) {
        return (
            <RegistrationDateStrip className="-mx-5">
                {/* The copy emphasises the date, so it needs Trans rather than t(). */}
                <Trans
                    t={t}
                    i18nKey={`${LOCALE_PREFIX}.confirm_message_before_deadline`}
                    values={{ registrationOpen: dates.registrationOpenDate }}
                />
            </RegistrationDateStrip>
        );
    }

    return (
        <RegistrationDateStrip className="-mx-5">
            <span className="max-sm:hidden">
                {t(`${LOCALE_PREFIX}.registration_deadline`, {
                    registrationDeadline: dates.registrationDeadline,
                    classStartDate: dates.classStartDate,
                })}
            </span>
            {/* The mobile line breaks after the deadline, so it needs Trans rather than t(). */}
            <span className="sm:hidden">
                <Trans
                    t={t}
                    i18nKey={`${LOCALE_PREFIX}.registration_deadline_mobile`}
                    values={{
                        registrationDeadline: dates.registrationDeadlineMobile,
                        classStartDate: dates.classStartDateMobile,
                    }}
                />
            </span>
        </RegistrationDateStrip>
    );
}
